// The report outbox and the device gap (fault-reports.md §4.4, R1628).
//
// **A lost link keeps the report on the device** (card 09 of round 077).
// When SEND meets no meta server (no answer, a timeout, or a gateway that
// answers for a meta server that is down), the REPORT sheet puts the
// report here and says THE REPORT WAITS. The outbox keeps it in local
// storage, so a reload keeps it too, and sends it when the browser says it
// is online again and every `REPORT_SHEET.outboxRetrySec` seconds. A report
// that goes out raises one notice with its reference. A report the meta
// server refuses for a reason a retry cannot cure (a broken part, no
// session) is dropped; a bucket refusal waits for the next try. The outbox
// keeps at most `outboxMax` reports, and a newer one pushes the oldest out.
// A storage that refuses the screenshot (a full quota) keeps the report
// without it.
//
// **One report a minute from one device** (card 08: ONE REPORT A MINUTE).
// The sheet reads the instant of the last report this device sent or put
// in the outbox, and SEND waits out `REPORT_SHEET.deviceGapSec`. The meta
// server's own buckets stand behind it, per account and per address.

import { REPORT_SHEET } from '../gen/presentation';
import { reportSheet as S } from '../gen/strings';
import {
  ApiError, CODE_TIMEOUT, CODE_UNAVAILABLE, type PlayerReportDraft, type PlayerReportReceipt,
} from './api';
import { base64Of, bytesOfBase64 } from '../util/base64';
import { storageStore, takeRenamed } from './renamed';

/** The storage key of the waiting reports. */
export const OUTBOX_KEY = 'ap.report.outbox';
/** The storage key of the instant of the last report, in ms. */
export const LAST_SENT_KEY = 'ap.report.last';
/** The old names of the two keys, before the short form `ap` (R1676). A
 *  read moves a value under an old name to its new name, so a report that
 *  waited in an old outbox still goes out. */
export const OLD_OUTBOX_KEY = 'aq.report.outbox';
export const OLD_LAST_SENT_KEY = 'aq.report.last';

/** True when an error says the meta server did not answer: the link is
 *  lost, and a later try can succeed. */
export function lostLink(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.code === CODE_UNAVAILABLE || err.code === CODE_TIMEOUT
    || err.status === 502 || err.status === 503 || err.status === 504;
}

/** The seconds this device must still wait before its next report, or 0. */
export function deviceGapLeftSec(store: Storage | null, nowMs: number): number {
  // `takeRenamed` answers null where the store refuses: no storage, no gap.
  const last = Number(takeRenamed(storageStore(store), LAST_SENT_KEY, OLD_LAST_SENT_KEY) ?? 0);
  if (!Number.isFinite(last) || last <= 0 || last > nowMs) return 0;
  return Math.max(0, Math.ceil(REPORT_SHEET.deviceGapSec - (nowMs - last) / 1000));
}

/** Notes that this device sent a report now. */
export function markSent(store: Storage | null, nowMs: number): void {
  try { store?.setItem(LAST_SENT_KEY, String(nowMs)); } catch { /* the gap is a courtesy */ }
}

/** A report as the outbox stores it: the screenshot as base64. */
interface Stored {
  draft: Omit<PlayerReportDraft, 'screenshot'>;
  shot: string;
}

/** What the outbox needs of the page. A test passes plain objects. */
export interface OutboxDeps {
  send: (d: PlayerReportDraft) => Promise<PlayerReportReceipt>;
  store: Storage | null;
  /** One notice line on the map. */
  notice: (line: string) => void;
}

export class ReportOutbox {
  private items: Stored[] = [];
  private timer = 0;
  private flushing = false;

  constructor(private readonly d: OutboxDeps) {
    try {
      const raw = takeRenamed(storageStore(d.store), OUTBOX_KEY, OLD_OUTBOX_KEY);
      const list = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(list)) {
        this.items = list.filter((x): x is Stored =>
          typeof x === 'object' && x !== null && typeof (x as Stored).draft === 'object' && typeof (x as Stored).shot === 'string');
      }
    } catch { this.items = []; }
  }

  /** How many reports wait. */
  get pending(): number { return this.items.length; }

  /** Puts one report in the outbox and starts the retries. */
  push(draft: PlayerReportDraft): void {
    const { screenshot, ...rest } = draft;
    this.items.push({ draft: rest, shot: screenshot ? base64Of(screenshot) : '' });
    if (this.items.length > REPORT_SHEET.outboxMax) this.items.splice(0, this.items.length - REPORT_SHEET.outboxMax);
    this.save();
    this.arm();
  }

  /**
   * Sends every waiting report, oldest first. It stops at a lost link or a
   * bucket refusal and keeps the rest; it drops a report the meta server
   * refuses for good. It answers how many went out.
   */
  async flush(): Promise<number> {
    if (this.flushing || this.items.length === 0) return 0;
    this.flushing = true;
    let sent = 0;
    try {
      while (this.items.length > 0) {
        const it = this.items[0];
        try {
          const receipt = await this.d.send({ ...it.draft, screenshot: it.shot ? bytesOfBase64(it.shot) : undefined });
          this.items.shift();
          sent++;
          this.d.notice(S.outbox_sent({ ref: receipt.ref }));
        } catch (err) {
          if (lostLink(err) || (err instanceof ApiError && err.status === 429)) break;
          this.items.shift();
        }
      }
    } finally {
      this.flushing = false;
      this.save();
      this.arm();
    }
    return sent;
  }

  /** Starts the retries: on the browser's online event, and on a timer
   *  while a report waits. The page calls it once. */
  start(): void {
    if (typeof window !== 'undefined') window.addEventListener('online', () => { void this.flush(); });
    this.arm();
    if (this.items.length > 0) void this.flush();
  }

  private arm(): void {
    if (typeof window === 'undefined') return;
    if (this.items.length === 0) {
      window.clearInterval(this.timer);
      this.timer = 0;
      return;
    }
    if (this.timer === 0) {
      this.timer = window.setInterval(() => { void this.flush(); }, REPORT_SHEET.outboxRetrySec * 1000);
    }
  }

  private save(): void {
    const s = this.d.store;
    if (!s) return;
    try {
      if (this.items.length === 0) s.removeItem(OUTBOX_KEY);
      else s.setItem(OUTBOX_KEY, JSON.stringify(this.items));
    } catch {
      // A full quota: keep the words and drop the pictures.
      try { s.setItem(OUTBOX_KEY, JSON.stringify(this.items.map((x) => ({ ...x, shot: '' })))); } catch { /* memory only */ }
    }
  }
}
